/**
 * SubstrateBatchSimpleForm Component
 *
 * Streamlined form for creating substrate batches in the deferred-count flow.
 * The recipe is the only structural input — actual bag count and bag size(s)
 * are recorded later via the Count Bags action when the cooled trolley is
 * wheeled in for inoculation.
 *
 * Flow:
 * 1. User selects recipe (and optional date/notes)
 * 2. Production card shows one nominal recipe's worth of materials
 * 3. Accept creates the batch and deducts recipe materials at recipe nominal
 */

import { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { DatePicker } from '@/components/ui/date-picker'
import { useRecipes } from '@/hooks/useSubstrateResources'
import { useSubstrateBatchWithMaterials } from '@/hooks/useSubstrateBatchWithMaterials'
import ProductionCardModal from './ProductionCardModal'
import RecipeForm from './RecipeForm'
import {
  AlertTriangle,
  Package,
  Loader2,
  Plus
} from 'lucide-react'
import { toast } from 'sonner'

const DEFAULT_BLOCK_WEIGHT_KG = 4.5

export default function SubstrateBatchSimpleForm({
  isOpen,
  onClose,
  onSave
}) {
  const [formData, setFormData] = useState({
    recipe_id: '',
    notes: '',
    batch_date: new Date()
  })
  const [error, setError] = useState(null)
  const [showProductionCard, setShowProductionCard] = useState(false)
  const [scaledIngredients, setScaledIngredients] = useState([])
  const [showRecipeForm, setShowRecipeForm] = useState(false)

  const { recipes, loading: recipesLoading, refetch: refetchRecipes } = useRecipes()
  const {
    createBatchWithMaterials,
    previewAllocations,
    isLoading
  } = useSubstrateBatchWithMaterials()

  const selectedRecipe = useMemo(() => {
    return recipes.find(r => r.id === formData.recipe_id) || null
  }, [recipes, formData.recipe_id])

  // Nominal yield for preview only (actual count is recorded later)
  const nominalBlocks = selectedRecipe?.blocks_per_batch || 54

  // Compute nominal ingredient quantities for production card preview
  useEffect(() => {
    if (!selectedRecipe?.ingredients) {
      setScaledIngredients([])
      return
    }
    setScaledIngredients(
      selectedRecipe.ingredients.map(ing => ({
        ...ing,
        scaled_quantity: ing.quantity_kg
      }))
    )
  }, [selectedRecipe])

  useEffect(() => {
    if (isOpen) {
      setFormData({
        recipe_id: '',
        notes: '',
        batch_date: new Date()
      })
      setError(null)
      setShowProductionCard(false)
      setScaledIngredients([])
      setShowRecipeForm(false)
    }
  }, [isOpen])

  const handleChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    setError(null)
  }, [])

  const handleShowProductionCard = useCallback(async () => {
    if (!formData.recipe_id) {
      setError('Please select a recipe')
      return
    }

    setError(null)

    // Enrich ingredients with allocation data so the production card can
    // display purchase units (e.g. "3 × 15kg bags").
    try {
      const preview = await previewAllocations(
        formData.recipe_id,
        nominalBlocks,
        DEFAULT_BLOCK_WEIGHT_KG
      )
      const enriched = scaledIngredients.map(ing => {
        const allocation = preview.allocations?.find(a => a.item_id === ing.item_id)
        return {
          ...ing,
          purchase_unit: allocation?.purchase_unit || ing.purchase_unit,
          conversion_factor: allocation?.conversion_factor || ing.conversion_factor,
          unit_of_measure: ing.unit_of_measure || 'kg'
        }
      })
      setScaledIngredients(enriched)
    } catch (err) {
      console.warn('Allocation preview failed:', err)
    }

    setShowProductionCard(true)
  }, [formData, scaledIngredients, previewAllocations, nominalBlocks])

  const handleAccept = useCallback(async () => {
    try {
      const result = await createBatchWithMaterials({
        recipe_id: formData.recipe_id,
        notes: formData.notes || null
        // No block_format_id, no total_blocks — captured later via Count Bags
      })

      if (result.material_warning) {
        toast.warning(result.material_warning)
      } else {
        toast.success(`Created batch ${result.batch_code}. Count bags when the trolley is wheeled in.`)
      }

      setShowProductionCard(false)
      onSave?.()
    } catch (err) {
      setError(err.message || 'Failed to create batch')
      setShowProductionCard(false)
    }
  }, [formData, createBatchWithMaterials, onSave])

  const activeRecipes = useMemo(() => {
    return recipes.filter(r => r.active !== false)
  }, [recipes])

  return (
    <>
      <Dialog open={isOpen && !showProductionCard} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-primary" />
              Create Substrate Batch
            </DialogTitle>
            <DialogDescription>
              Pick a recipe to start a mix. Bag count and size are recorded later when the cooled trolley is wheeled in.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {error && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="recipe">Recipe</Label>
                <Select
                  value={formData.recipe_id}
                  onValueChange={(value) => {
                    if (value === '__create_new__') {
                      setShowRecipeForm(true)
                    } else {
                      handleChange('recipe_id', value)
                    }
                  }}
                  disabled={recipesLoading}
                >
                  <SelectTrigger id="recipe">
                    <SelectValue placeholder="Select a recipe..." />
                  </SelectTrigger>
                  <SelectContent>
                    {activeRecipes.map(recipe => (
                      <SelectItem key={recipe.id} value={recipe.id}>
                        {recipe.name}
                      </SelectItem>
                    ))}
                    <SelectItem value="__create_new__" className="text-primary">
                      <span className="flex items-center gap-1">
                        <Plus className="h-3 w-3" />
                        Create New
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Batch Date</Label>
                <DatePicker
                  date={formData.batch_date}
                  onDateChange={(date) => handleChange('batch_date', date)}
                  placeholder="Select date"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes (optional)</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                placeholder="Any additional notes..."
                rows={2}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleShowProductionCard}
              disabled={recipesLoading || isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Loading...
                </>
              ) : (
                'Create Batch'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ProductionCardModal
        isOpen={showProductionCard}
        onClose={() => setShowProductionCard(false)}
        onAccept={handleAccept}
        recipe={selectedRecipe}
        blockFormat={null}
        totalBlocks={nominalBlocks}
        ingredients={scaledIngredients}
        isLoading={isLoading}
        nominal
      />

      <RecipeForm
        isOpen={showRecipeForm}
        onClose={() => setShowRecipeForm(false)}
        onSave={(newRecipe) => {
          refetchRecipes()
          if (newRecipe?.id) {
            setFormData(prev => ({ ...prev, recipe_id: newRecipe.id }))
          }
          setShowRecipeForm(false)
        }}
      />
    </>
  )
}
